import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, ShieldCheck, Globe, Sparkles, Lock, Layers } from 'lucide-react';
import { Button } from './ui/button';
import { BRANDS, FEATURES } from '../mock';
import BrandImage from './BrandImage';
import { useTheme } from '../context/ThemeContext';

const ICONS = { ShieldCheck, Layers, Globe, Lock };

const FloatingCard = ({ style, brandSlug, brandColor, brandDomain, label, amount, coin, delay, className = '' }) => {
  const { isDark } = useTheme();
  const iconColor = (brandColor || '#8247E5').replace('#', '');
  return (
    <div
      className={`absolute w-56 bg-white/95 dark:bg-neutral-900/85 backdrop-blur-md rounded-2xl p-5 border border-white/60 dark:border-violet-500/30 shadow-2xl ${className}`}
      style={{
        ...style,
        animation: `float-y ${6 + delay}s ease-in-out infinite`,
        animationDelay: `${delay}s`,
        boxShadow: isDark
          ? '0 25px 60px -15px rgba(130,71,229,0.55), 0 0 0 1px rgba(168,85,247,0.35) inset'
          : '0 25px 50px -12px rgba(130,71,229,0.35), 0 0 0 1px rgba(255,255,255,0.5) inset',
      }}>
      <img
        src={`https://cdn.simpleicons.org/${brandSlug}/${iconColor}`}
        className="h-10 object-contain"
        alt={label}
        loading="lazy"
        data-fallback="0"
        onError={(e) => {
          const el = e.currentTarget;
          const s = parseInt(el.dataset.fallback || '0');
          if (s === 0 && brandDomain) {
            el.dataset.fallback = '1';
            el.src = `https://logo.clearbit.com/${brandDomain}`;
          } else if (s <= 1 && brandDomain) {
            el.dataset.fallback = '2';
            el.src = `https://logo.uplead.com/${brandDomain}`;
          } else {
            el.onerror = null;
            el.style.display = 'none';
          }
        }}
      />
      <div className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">{label}</div>
      <div className="text-2xl font-bold mt-1 text-neutral-900 dark:text-white">${amount}</div>
      <div className="mt-3 flex items-center gap-1 text-xs text-[#8247E5] dark:text-[#C084FC] font-semibold">
        <Zap className="w-3.5 h-3.5" /> Paid with {coin}
      </div>
    </div>
  );
};

const HeroSection = () => {
  const heroRef = useRef(null);
  const { isDark } = useTheme();
  const marqueeBrands = [...BRANDS.filter(b => b.featured), ...BRANDS.filter(b => b.featured)];
  const heroBg = isDark
    ? 'radial-gradient(1200px circle at 20% 0%, rgba(130,71,229,0.35) 0%, transparent 60%), radial-gradient(1200px circle at 80% 100%, rgba(168,85,247,0.25) 0%, transparent 50%), hsl(258 30% 5%)'
    : 'radial-gradient(1200px circle at 20% 0%, #F3EBFF 0%, transparent 60%), radial-gradient(1200px circle at 80% 100%, #FCE7F3 0%, transparent 50%), #FAFAFB';

  return (
    <section ref={heroRef} className="relative overflow-hidden isolate"
      style={{
        background: isDark
          ? 'radial-gradient(1200px circle at 20% 0%, rgba(130,71,229,0.35) 0%, transparent 60%), radial-gradient(1200px circle at 80% 100%, rgba(168,85,247,0.25) 0%, transparent 50%), hsl(258 30% 5%)'
          : 'radial-gradient(1200px circle at 20% 0%, #F3EBFF 0%, transparent 60%), radial-gradient(1200px circle at 80% 100%, #FCE7F3 0%, transparent 50%), #FAFAFB',
      }}>
      {/* Aurora — soft flowing purple blobs, visible only in dark mode */}
      <div className="aurora" aria-hidden="true"><div className="blob" /></div>
      <div className="glow-ring animate-blob" style={{ width: 400, height: 400, background: '#A855F7', top: '10%', left: '-5%' }} />
      <div className="glow-ring animate-blob" style={{ width: 350, height: 350, background: '#8247E5', bottom: '10%', right: '5%', animationDelay: '3s' }} />
      <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
      <div className="mouse-glow hidden lg:block" style={{ top: 0, left: 0 }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16 md:pt-24 md:pb-24 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal in-view">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-violet-200 text-xs font-medium text-violet-700 mb-6 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8247E5] pulse-glow" />
              Est. 2026 · Non-custodial · Crypto-native
              <Sparkles className="w-3 h-3 text-[#8247E5]" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-[68px] font-bold text-neutral-900 dark:text-white leading-[1.02]">
              Live on <span className="gradient-text">crypto.</span><br />
              Spend it <span className="relative inline-block">
                anywhere.
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" preserveAspectRatio="none">
                  <path d="M0 5 Q 50 0, 100 4 T 200 3" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" className="text-[#8247E5] dark:text-[#C084FC]" />
                </svg>
              </span>
            </h1>
            <p className="mt-6 text-lg text-neutral-600 dark:text-neutral-300 max-w-xl leading-relaxed">
              Turn your crypto into real-world spending. Buy gift cards from Amazon, Netflix, Steam and hundreds more, top up any phone worldwide, and swap between 20+ coins. All in one place. No bank. No sign-up needed to start.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/browse">
                <Button className="h-13 px-7 shine-btn text-white text-base font-semibold rounded-xl shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 transition-shadow">
                  Explore gift cards
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/swap">
                <Button variant="outline" className="h-13 px-7 border-neutral-300 dark:border-violet-500/40 hover:border-neutral-900 dark:hover:border-[#C084FC] text-base font-semibold rounded-xl bg-white/80 dark:bg-neutral-900/60 backdrop-blur text-neutral-900 dark:text-white dark:hover:text-[#C084FC]">
                  Swap assets
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-neutral-700 dark:text-neutral-300">
              <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#8247E5] dark:text-[#C084FC]" /> Instant delivery</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#8247E5] dark:text-[#C084FC]" /> No account needed</div>
              <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-[#8247E5] dark:text-[#C084FC]" /> Available worldwide</div>
            </div>
          </div>

          {/* Floating cards */}
          <div className="relative hidden lg:block">
            <div className="relative h-[520px]">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(130,71,229,0.15) 0%, transparent 70%)' }} />
              <FloatingCard style={{ top: 0, left: 40, transform: 'rotate(-6deg)' }} brandSlug="spotify" brandColor="#1DB954" brandDomain="spotify.com" label="Spotify Premium" amount="30.00" coin="BTC" delay={0} />
              <FloatingCard style={{ top: 60, right: 0, transform: 'rotate(5deg)' }} brandSlug="netflix" brandColor="#E50914" brandDomain="netflix.com" label="Netflix Subscription" amount="30.00" coin="ETH" delay={1} />
              <FloatingCard style={{ bottom: 40, left: 0, transform: 'rotate(-3deg)' }} brandSlug="steam" brandColor={isDark ? '#66C0F4' : '#1B2838'} brandDomain="steampowered.com" label="Steam Wallet" amount="50.00" coin="USDT" delay={2} />
              <FloatingCard style={{ bottom: 100, right: 40, transform: 'rotate(8deg)' }} brandSlug="playstation" brandColor={isDark ? '#6D9BFB' : '#003791'} brandDomain="playstation.com" label="PlayStation Store" amount="100.00" coin="SOL" delay={3} />
            </div>
          </div>
        </div>

        {/* FEATURE-BASED VALUE PROPS (no vanity metrics) */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {FEATURES.map((s, i) => {
            const Icon = ICONS[s.icon] || Sparkles;
            return (
              <div key={i} className="bg-white/70 dark:bg-neutral-900/50 backdrop-blur rounded-2xl border border-violet-100 dark:border-violet-900/40 p-5 hover:border-violet-300 dark:hover:border-violet-700 hover:-translate-y-0.5 transition-all">
                <div className="w-9 h-9 rounded-lg mb-3 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8247E515, #A855F715)' }}>
                  <Icon className="w-4 h-4 text-[#8247E5] dark:text-[#C084FC]" />
                </div>
                <div className="font-display font-bold text-neutral-900 dark:text-white">{s.title}</div>
                <div className="text-xs md:text-sm text-neutral-600 dark:text-neutral-400 mt-1 leading-relaxed">{s.desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Brands marquee */}
      <div className="border-y border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-900/60 backdrop-blur py-6 overflow-hidden relative">
        <div className="absolute left-0 top-0 bottom-0 w-32 z-10 pointer-events-none" style={{ background: isDark ? 'linear-gradient(90deg, hsl(258 30% 5%), transparent)' : 'linear-gradient(90deg, #FAFAFB, transparent)' }} />
        <div className="absolute right-0 top-0 bottom-0 w-32 z-10 pointer-events-none" style={{ background: isDark ? 'linear-gradient(270deg, hsl(258 30% 5%), transparent)' : 'linear-gradient(270deg, #FAFAFB, transparent)' }} />
        <div className="flex gap-12 animate-marquee whitespace-nowrap">
          {marqueeBrands.map((b, i) => (
            <div key={i} className="flex items-center gap-3 shrink-0">
              <BrandImage brand={b} className="h-6 w-auto opacity-70 hover:opacity-100 transition" tileClassName="h-6 w-10 rounded text-[10px] opacity-70" />
              <span className="text-sm font-medium text-neutral-500">{b.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
